/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ElementRef } from '@angular/core';

export class PositionService {
  /**
   * Provides read-only equivalent of jQuery's position function:
   * http://api.jquery.com/position/
   */
  static position(element: ElementRef): { width: number; height: number; top: number; left: number } {
    const nativeEl: any = element.nativeElement;
    const elBCR = PositionService.offset(nativeEl);
    let offsetParentBCR = { top: 0, left: 0 };
    const offsetParentEl = PositionService.parentOffsetEl(nativeEl);
    if (offsetParentEl !== PositionService.document) {
      offsetParentBCR = PositionService.offset(offsetParentEl);
      offsetParentBCR.top += offsetParentEl.clientTop - offsetParentEl.scrollTop;
      offsetParentBCR.left += offsetParentEl.clientLeft - offsetParentEl.scrollLeft;
    }

    const boundingClientRect = nativeEl.getBoundingClientRect();
    return {
      width: boundingClientRect.width || nativeEl.offsetWidth,
      height: boundingClientRect.height || nativeEl.offsetHeight,
      top: elBCR.top - offsetParentBCR.top,
      left: elBCR.left - offsetParentBCR.left,
    };
  }

  /**
   * Provides read-only equivalent of jQuery's offset function:
   * http://api.jquery.com/offset/
   */
  static offset(element: ElementRef): { width: number; height: number; top: number; left: number } {
    const nativeEl: any = element.nativeElement;
    const boundingClientRect = nativeEl.getBoundingClientRect();
    return {
      width: boundingClientRect.width || nativeEl.offsetWidth,
      height: boundingClientRect.height || nativeEl.offsetHeight,
      top: boundingClientRect.top
        + (PositionService.window.pageYOffset || PositionService.document.documentElement.scrollTop),
      left: boundingClientRect.left
        + (PositionService.window.pageXOffset || PositionService.document.documentElement.scrollLeft),
    };
  }

  /**
   * Provides coordinates for the targetEl in relation to hostEl
   */
  static positionElements(host: ElementRef,
                          target: ElementRef,
                          positionStr: any,
                          appendToBody: any): { top: number; left: number } {
    const hostEl: any = host.nativeElement;
    const targetEl: any = target.nativeElement;
    const positionStrParts = positionStr.split('-');
    const pos0 = positionStrParts[0];
    const pos1 = positionStrParts[1] || 'center';
    const hostElPos = appendToBody ?
      PositionService.offset(hostEl) :
      PositionService.position(hostEl);
    const targetElWidth = targetEl.offsetWidth;
    const targetElHeight = targetEl.offsetHeight;

    const shiftWidth = {
      center(): number {
        return hostElPos.left + hostElPos.width / 2 - targetElWidth / 2;
      },
      left(): number {
        return hostElPos.left;
      },
      right(): number {
        return hostElPos.left + hostElPos.width;
      },
    };

    const shiftHeight = {
      center(): number {
        return hostElPos.top + hostElPos.height / 2 - targetElHeight / 2;
      },
      top(): number {
        return hostElPos.top;
      },
      bottom(): number {
        return hostElPos.top + hostElPos.height;
      },
    };

    let targetElPos: { top: number; left: number };
    switch (pos0) {
      case 'right':
        targetElPos = {
          top: shiftHeight[pos1](),
          left: shiftWidth[pos0](),
        };
        break;
      case 'left':
        targetElPos = {
          top: shiftHeight[pos1](),
          left: hostElPos.left - targetElWidth,
        };
        break;
      case 'bottom':
        targetElPos = {
          top: shiftHeight[pos0](),
          left: shiftWidth[pos1](),
        };
        break;
      default:
        targetElPos = {
          top: hostElPos.top - targetElHeight,
          left: shiftWidth[pos1](),
        };
        break;
    }

    return targetElPos;
  }

  private static get window(): Window {
    return window;
  }

  private static get document(): Document {
    return window.document;
  }

  private static getStyle(nativeEl: any, cssProp: string): any {
    // IE
    if (nativeEl.currentStyle) {
      return nativeEl.currentStyle[cssProp];
    }

    if (PositionService.window.getComputedStyle) {
      return PositionService.window.getComputedStyle(nativeEl)[cssProp];
    }
    // finally try and get inline style
    return nativeEl.style[cssProp];
  }

  /**
   * Checks if a given element is statically positioned
   * @param nativeEl - raw DOM element
   */
  private static isStaticPositioned(nativeEl: any): any {
    return (PositionService.getStyle(nativeEl, 'position') || 'static') === 'static';
  }

  /**
   * returns the closest, non-statically positioned parentOffset of a given element
   * @param nativeEl
   */
  private static parentOffsetEl(nativeEl: any): any {
    let offsetParent = nativeEl.offsetParent || PositionService.document;
    while (offsetParent && offsetParent !== PositionService.document &&
    PositionService.isStaticPositioned(offsetParent)) {
      offsetParent = offsetParent.offsetParent;
    }
    return offsetParent || PositionService.document;
  }
}
